<template>
  <modal
    v-if="collectionOrId !== null"
    :id="modalIdShowColumns"
    :title="$i18n.t('list_visible_columns')"
    @modal-closed="onClosed"
    @modal-opened="onOpened"
  >
    <choice-list
      class="show-in-list"
      :list="availableColumns"
      :aggregations="aggregations"
      :name="'showColumns'"
      :value="showInList"
      @change="toggleShowInList"
      type="checkboxlist"
    >
      <template slot="aggregation" slot-scope="aggregation">
        <radio-group
          v-if="showAggregations(aggregation.colData)"
          :active-id="activeId(aggregation.colData)"
          :options="aggregateOptions[aggregation.colData]"
          @change="toggleAggregation(aggregation.colData, $event)"
        ></radio-group>
      </template>
    </choice-list>
  </modal>
</template>

<script>
import RadioGroup from './RadioGroup.vue'
import ChoiceList from './ChoiceList.vue'
import Modal from '../../modal/Modal'
import { cloneDeep, isEmpty, isEqual, isString, xor } from 'lodash'
import { parseMultiColumnName } from '../../../utils/list'
import { createStorage } from '../../../utils'
import EventBus from '../../../utils/event-bus'
import { AGGREGATION_OFF } from '../lib/list-constants'

export default {
  name: 'ShowInListModal',
  components: {
    Modal,
    ChoiceList,
    RadioGroup,
  },
  data() {
    return {
      aggregationCache: [],
    }
  },
  computed: {
    aggregateColumn() {
      return this.$get('aggregateColumn')
    },
    aggregations: {
      get() {
        return this.$get('aggregations')
      },
      set(aggregations) {
        this.$dispatch('setAggregations', { aggregations })
      },
    },
    aggregateOptions() {
      if (isEmpty(this.aggregateColumn)) return {}
      return Object.entries(this.aggregateColumn).reduce((accumulator, [key, { functions }]) => {
        functions = ['Off', ...functions]
        accumulator[key] = { functions }
        return accumulator
      }, {})
    },
    availableColumns() {
      const columns = []
      for (const availableColumn of this.availableInList) {
        const column = this.columns.find((column) => parseMultiColumnName(column.data) === availableColumn)
        if (column) columns.push([parseMultiColumnName(column.data), column.title || parseMultiColumnName(column.data)])
      }
      return columns
    },
    availableInList() {
      return this.$get('availableInList')
    },
    collectionOrId() {
      return this.$get('collectionOrId')
    },
    columns() {
      return this.$get('columns')
    },
    currentParameters() {
      return this.$get('currentParameters')
    },
    modalIdShowColumns() {
      return `show-columns-modal-${this.collectionOrId}-${this.panelId}`
    },
    panelId() {
      const _panelId = this.$get('panelId')
      return isString(_panelId) ? _panelId : null
    },
    id() {
      return this.$get('id')
    },
    showInList: {
      get() {
        return this.$get('showInList')
      },
      set(showInList) {
        this.$dispatch('setShowInList', { showInList })
        this.localStore.setItemValue('showInList', showInList)
      },
    },
    storageKey() {
      return this.$get('storageKey')
    },
  },
  methods: {
    showAggregations(colData) {
      return !isEmpty(this.aggregateColumn) && !isEmpty(this.aggregateColumn[colData])
    },
    activeId(colData) {
      const aggregation = this.aggregations.find(({ column }) => column === colData)
      return aggregation ? aggregation.aggr : AGGREGATION_OFF
    },
    hasAggregateFunction(id) {
      return this.aggregateColumn ? Object.keys(this.aggregateColumn).includes(id) : false
    },
    onClosed() {
      if (!isEqual(this.aggregationCache, this.aggregations)) {
        EventBus.$emit(`apply-filter-${this.id}`, {
          id: this.id,
          type: 'aggregation',
        })
      }
    },
    onOpened() {
      this.aggregationCache = cloneDeep(this.aggregations)
    },
    toggleAggregation(id, { functionName }) {
      const aggregations = cloneDeep(this.aggregations).filter((a) => a.column !== id)
      aggregations.push({ column: id, aggr: functionName })

      this.aggregations = cloneDeep(aggregations)

      if (!isEmpty(this.aggregations)) {
        this.localStore.setItemValue('aggregations', this.aggregations)
      } else {
        this.localStore.removeItemValue('aggregations')
      }
    },
    toggleShowInList(colData) {
      const newValue = xor(this.showInList, [colData])
      if (newValue.length) {
        this.showInList = this.availableInList.filter((data) => newValue.includes(data))
        this.localStore.setItemValue('showInList', this.showInList)
      }
    },
  },
  created() {
    this.localStore = createStorage(this.storageKey)
  },
}
</script>
